import { useTranslation } from "react-i18next";
import { openingStatus, type OpeningHours, type OpeningStatus } from "@majourneys/shared";
import { useVenueClock } from "./use-venue-clock";
import { weekdayName } from "./weekday";

export interface OpenNowPillProps {
  hours: OpeningHours | undefined;
  /** Pins the clock (tests). Without it the pill appears after mount. */
  now?: Date;
  className?: string;
  /** Narrow places (a card in a row): the detail wraps under the label
   *  instead of being cut off with an ellipsis. */
  wrap?: boolean;
}

/* "B · Airbnb pill" (owner, 2026-09-26). Each state keeps its own ink so the
   label clears 4.5:1 on its own tint rather than on white. */
const TONES = {
  open: { bg: "#EAF6EE", dot: "#2F855A", label: "#1F5E3B", detail: "#3F6B52" },
  closing_soon: { bg: "#FDF3E1", dot: "#C27A0E", label: "#7A4A06", detail: "#7F5616" },
  closed: { bg: "#F3F1FA", dot: "#8A8CA3", label: "#110E39", detail: "#5a5d77" },
} as const;

function useDetail(status: OpeningStatus): string | null {
  const { t, i18n } = useTranslation();
  if (status.state === "open") return t("activity.restaurant.until", { time: status.until });
  if (status.state === "closing_soon") return status.until ?? null;
  if (!status.opensAt) return null;
  if (status.opensInDays === 0) return t("activity.restaurant.opens_at", { time: status.opensAt });
  if (status.opensInDays === 1) {
    return t("activity.restaurant.opens_tomorrow_at", { time: status.opensAt });
  }
  return t("activity.restaurant.opens_day_at", {
    day: weekdayName(status.opensDay ?? "mon", i18n.language),
    time: status.opensAt,
  });
}

function Pill({ status, className, wrap = false }: { status: OpeningStatus; className?: string; wrap?: boolean }) {
  const { t } = useTranslation();
  const detail = useDetail(status);
  const tone = TONES[status.state];
  const live = status.state !== "closed";
  const label =
    status.state === "open"
      ? t("activity.restaurant.open_now")
      : status.state === "closing_soon"
      ? t("activity.restaurant.closes_soon")
      : t("activity.restaurant.closed");

  return (
    <p
      data-testid="open-now-pill"
      data-state={status.state}
      className={`inline-flex max-w-full min-w-0 items-center gap-2 py-1.5 pe-3.5 ps-3 font-sans text-[13px] leading-[18px] ${
        wrap ? "flex-wrap gap-y-0 rounded-[12px]" : "rounded-full"
      } ${className ?? ""}`}
      style={{ background: tone.bg }}
    >
      <span aria-hidden="true" className="relative flex h-2 w-2 shrink-0">
        {/* The halo, not the dot, pulses, so the dot itself never blinks.
            motion-safe: a visitor who asked for reduced motion gets a still dot. */}
        {live && (
          <span
            className="absolute inset-0 rounded-full opacity-50 motion-safe:animate-ping"
            style={{ background: tone.dot, animationDuration: "2.2s" }}
          />
        )}
        <span className="relative h-2 w-2 rounded-full" style={{ background: tone.dot }} />
      </span>
      <span className="shrink-0 font-semibold" style={{ color: tone.label }}>
        {label}
      </span>
      {detail && (
        <span className="min-w-0 break-words" style={{ color: tone.detail }}>
          {status.state === "closing_soon" ? `· ${detail}` : detail}
        </span>
      )}
    </p>
  );
}

/**
 * Open / closing soon / closed, on the venue's Casablanca clock.
 *
 * Renders nothing when the hours are unknown, and nothing during SSR and the
 * first client render (see useVenueClock), then re-evaluates every minute so
 * a page left open across the closing time flips on its own.
 */
export function OpenNowPill({ hours, now, className, wrap }: OpenNowPillProps) {
  const clock = useVenueClock(now);
  if (!hours || !clock) return null;
  const status = openingStatus(hours, clock);
  if (!status) return null;
  return <Pill status={status} className={className} wrap={wrap} />;
}
